import { animate, motion, motionValue } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Edge } from "./Edge.js";
import { NodeCard } from "./NodeCard.js";

const MIN_ZOOM = 0.22;
const MAX_ZOOM = 2.4;

export function OrgCanvas({
  nodes,
  targets,
  depths,
  byId,
  rootIds,
  radial,
  mode = "vertical",
  edgeAnchor = "center",
  cardScale,
  selectedId,
  matchIds,
  highlightDept,
  panX,
  panY,
  zoom,
  onSelect,
  onAddChild,
  lineage,
  isLight,
  C,
}) {
  const containerRef = useRef(null);
  const mvRef = useRef(new Map());
  const seenRef = useRef(new Set());
  const firstRunRef = useRef(true);
  const dragRef = useRef(null);

  const getMV = useCallback(
    (id) => {
      let mv = mvRef.current.get(id);
      if (!mv) {
        mv = { x: motionValue(0), y: motionValue(0) };
        mvRef.current.set(id, mv);
      }
      return mv;
    },
    []
  );

  const rootOf = useCallback(
    (id) => {
      let cur = byId.get(id);
      const guard = new Set();
      while (cur?.parentId && byId.has(cur.parentId) && !guard.has(cur.id)) {
        guard.add(cur.id);
        cur = byId.get(cur.parentId);
      }
      return cur?.id ?? id;
    },
    [byId]
  );

  useEffect(() => {
    const firstRun = firstRunRef.current;

    nodes.forEach((node) => {
      const t = targets.get(node.id);
      if (!t) return;
      const mv = getMV(node.id);
      const depth = depths.get(node.id) ?? 0;

      if (!seenRef.current.has(node.id)) {
        const parentTarget = node.parentId ? targets.get(node.parentId) : undefined;
        const origin = firstRun
          ? targets.get(rootOf(node.id)) ?? t
          : parentTarget ?? t;
        mv.x.set(origin.x);
        mv.y.set(origin.y);
        seenRef.current.add(node.id);
      }

      const delay = firstRun ? 0.15 + depth * 0.1 : depth * 0.045;
      animate(mv.x, t.x, { type: "spring", stiffness: 96, damping: 17.5, mass: 1, delay });
      animate(mv.y, t.y, { type: "spring", stiffness: 96, damping: 17.5, mass: 1, delay });
    });

    const alive = new Set(nodes.map((n) => n.id));
    mvRef.current.forEach((_, id) => {
      if (!alive.has(id)) {
        mvRef.current.delete(id);
        seenRef.current.delete(id);
      }
    });

    firstRunRef.current = false;
  }, [targets, nodes, depths, getMV, rootOf]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const z0 = zoom.get();
      const z1 = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z0 * Math.exp(-e.deltaY * 0.0014)));
      const k = z1 / z0;
      panX.set(cx - (cx - panX.get()) * k);
      panY.set(cy - (cy - panY.get()) * k);
      zoom.set(z1);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [panX, panY, zoom]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      px: panX.get(),
      py: panY.get(),
      moved: false,
    };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    if (d.moved) {
      panX.set(d.px + dx);
      panY.set(d.py + dy);
    }
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && !d.moved) onSelect(null);
  };

  const selectedLineage = useMemo(
    () => (selectedId ? lineage(selectedId) : null),
    [selectedId, lineage]
  );

  const isNodeDim = useCallback(
    (n) => {
      if (matchIds && !matchIds.has(n.id)) return true;
      if (highlightDept && n.department !== highlightDept) return true;
      return false;
    },
    [matchIds, highlightDept]
  );

  const edges = useMemo(() => {
    return nodes
      .filter((n) => n.parentId && byId.has(n.parentId))
      .map((n) => {
        let state = "idle";
        if (selectedId && selectedLineage) {
          if (selectedLineage.ancestors.has(n.id)) state = "flow";
          else if (selectedLineage.descendants.has(n.id)) state = "active";
        }
        if (state === "idle" && (matchIds || highlightDept) && isNodeDim(n)) state = "dim";
        return { id: n.id, parentId: n.parentId, state, color: byId.get(n.id)?.color ?? "#fff" };
      });
  }, [nodes, byId, selectedId, selectedLineage, matchIds, highlightDept, isNodeDim]);

  const filterActive = Boolean(matchIds || highlightDept);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 cursor-grab touch-none overflow-hidden active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => (dragRef.current = null)}
    >
      <motion.div
        className="absolute left-0 top-0 h-0 w-0"
        style={{ x: panX, y: panY, scale: zoom, transformOrigin: "0 0" }}
      >
        <svg className="absolute overflow-visible" style={{ left: 0, top: 0 }}>
          <defs>
            <pattern id="dots" width="36" height="36" patternUnits="userSpaceOnUse">
              <circle
                cx="1.4"
                cy="1.4"
                r="1.4"
                fill={isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.055)"}
              />
            </pattern>
          </defs>
          <rect x={-9000} y={-9000} width={18000} height={18000} fill="url(#dots)" />

          {edges.map((e) => {
            const p = getMV(e.parentId);
            const c = getMV(e.id);
            return (
              <Edge
                key={e.id}
                px={p.x}
                py={p.y}
                cx={c.x}
                cy={c.y}
                state={filterActive && e.state === "idle" ? "dim" : e.state}
                C={C}
                anchor={edgeAnchor}
                mode={mode}
              />
            );
          })}
        </svg>

        {nodes.map((n, i) => {
          const mv = getMV(n.id);
          return (
            <NodeCard
              key={n.id}
              node={n}
              mx={mv.x}
              my={mv.y}
              depth={depths.get(n.id) ?? 0}
              order={i}
              isRoot={rootIds.has(n.id)}
              selected={n.id === selectedId}
              dimmed={isNodeDim(n)}
              radial={radial}
              cardScale={cardScale * (n.scale ?? 1)}
              onSelect={onSelect}
              onAddChild={onAddChild}
              isLight={isLight}
              C={C}
            />
          );
        })}
      </motion.div>
    </div>
  );
}
